/**
 * CurrencyContext - Handles currency conversion based on user's actual location
 * 
 * Features:
 * - Auto-detects user currency from IP-based geolocation (independent from Amazon region selector)
 * - Fetches live exchange rates from backend (cached hourly)
 * - Provides price formatting and conversion utilities
 * - Base currency: EUR (Shopify store currency)
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '@/lib/api';

const CurrencyContext = createContext(null);

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  // EUR countries
  PT: 'EUR', ES: 'EUR', DE: 'EUR', FR: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
  // GBP
  GB: 'GBP', UK: 'GBP',
  // USD
  US: 'USD',
  // AUD
  AU: 'AUD',
  // CAD
  CA: 'CAD',
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
};

// Fallback rates if API fails
const FALLBACK_RATES = {
  EUR: 1.0,
  USD: 1.08,
  GBP: 0.85,
  AUD: 1.65,
  CAD: 1.47,
};

export const CurrencyProvider = ({ children }) => {
  const [userCurrency, setUserCurrency] = useState('EUR'); // Default to EUR
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  const userCurrencySymbol = useMemo(() => {
    return CURRENCY_SYMBOLS[userCurrency] || '€';
  }, [userCurrency]);

  // Detect user's location via IP and set currency
  const detectUserCurrency = useCallback(async () => {
    try {
      // Check localStorage first for cached currency
      const cachedCurrency = localStorage.getItem('coolgirl-user-currency');
      const cachedTime = localStorage.getItem('coolgirl-user-currency-time');
      
      // Use cached value if less than 24 hours old
      if (cachedCurrency && cachedTime) {
        const hoursSinceCached = (Date.now() - parseInt(cachedTime)) / (1000 * 60 * 60);
        if (hoursSinceCached < 24 && CURRENCY_SYMBOLS[cachedCurrency]) {
          setUserCurrency(cachedCurrency);
          return;
        }
      }

      // Fetch user's country from IP
      const response = await fetch('http://ip-api.com/json/?fields=countryCode');
      if (response.ok) {
        const data = await response.json();
        const countryCode = data.countryCode?.toUpperCase();
        const detectedCurrency = COUNTRY_CURRENCY_MAP[countryCode] || 'EUR';
        
        setUserCurrency(detectedCurrency);
        
        // Cache the result
        localStorage.setItem('coolgirl-user-currency', detectedCurrency);
        localStorage.setItem('coolgirl-user-currency-time', Date.now().toString());
      }
    } catch (error) {
      console.warn('Failed to detect user currency, defaulting to EUR:', error);
      setUserCurrency('EUR');
    }
  }, []);

  // Fetch exchange rates from backend
  const fetchRates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/currency/rates`);
      if (response.ok) {
        const data = await response.json();
        setRates(data.rates);
        setLastUpdated(data.last_updated);
      }
    } catch (error) {
      console.warn('Failed to fetch exchange rates, using fallback:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Detect currency and fetch rates on mount
  useEffect(() => {
    detectUserCurrency();
    fetchRates();
    
    // Refresh rates every hour
    const interval = setInterval(fetchRates, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [detectUserCurrency, fetchRates]);

  /**
   * Convert price from EUR (base) to user's currency
   * @param {number} priceInEur - Price in EUR
   * @returns {number} - Price in user's currency
   */
  const convertFromEur = useCallback((priceInEur) => {
    if (priceInEur === null || priceInEur === undefined) return null;
    const numPrice = typeof priceInEur === 'string' ? parseFloat(priceInEur) : priceInEur;
    if (isNaN(numPrice)) return null;
    
    const rate = rates[userCurrency] || 1;
    return Math.round(numPrice * rate * 100) / 100;
  }, [rates, userCurrency]);

  /**
   * Convert price from any currency to user's currency
   * @param {number} price - Price amount
   * @param {string} fromCurrency - Source currency code (default: EUR)
   * @returns {number} - Price in user's currency
   */
  const convertPrice = useCallback((price, fromCurrency = 'EUR') => {
    if (price === null || price === undefined) return null;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return null;
    
    // First convert to EUR if not already
    const fromRate = rates[fromCurrency.toUpperCase()] || 1;
    const priceInEur = numPrice / fromRate;
    
    // Then convert to user's currency
    const toRate = rates[userCurrency] || 1;
    return Math.round(priceInEur * toRate * 100) / 100;
  }, [rates, userCurrency]);

  /**
   * Format price with currency symbol
   * @param {number} price - Price in EUR (will be converted)
   * @param {object} options - Formatting options
   * @returns {string} - Formatted price string
   */
  const formatPrice = useCallback((price, options = {}) => {
    const {
      fromCurrency = 'EUR',
      showOriginal = false,
      decimals = 2
    } = options;

    if (price === null || price === undefined) return '';
    
    const convertedPrice = convertPrice(price, fromCurrency);
    if (convertedPrice === null) return '';
    
    const formatted = `${userCurrencySymbol}${convertedPrice.toFixed(decimals)}`;
    
    if (showOriginal && fromCurrency !== userCurrency) {
      const originalSymbol = CURRENCY_SYMBOLS[fromCurrency.toUpperCase()] || '€';
      return `${formatted} (${originalSymbol}${parseFloat(price).toFixed(decimals)})`;
    }
    
    return formatted;
  }, [convertPrice, userCurrency, userCurrencySymbol]);

  /**
   * Format price that's already in user's currency (no conversion)
   * @param {number} price - Price already in user's currency
   * @returns {string} - Formatted price string
   */
  const formatLocalPrice = useCallback((price, decimals = 2) => {
    if (price === null || price === undefined) return '';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '';
    return `${userCurrencySymbol}${numPrice.toFixed(decimals)}`;
  }, [userCurrencySymbol]);

  const value = {
    // Current user settings
    userCurrency,
    userCurrencySymbol,
    baseCurrency: 'EUR',
    
    // Exchange rates
    rates,
    lastUpdated,
    loading,
    
    // Conversion functions
    convertFromEur,
    convertPrice,
    formatPrice,
    formatLocalPrice,
    
    // Utility
    refreshRates: fetchRates,
    getCurrencySymbol: (code) => CURRENCY_SYMBOLS[code?.toUpperCase()] || '€',
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    // Return default values if context is not available
    return {
      userCurrency: 'EUR',
      userCurrencySymbol: '€',
      baseCurrency: 'EUR',
      rates: FALLBACK_RATES,
      loading: false,
      convertFromEur: (price) => price,
      convertPrice: (price) => price,
      formatPrice: (price) => `€${parseFloat(price || 0).toFixed(2)}`,
      formatLocalPrice: (price) => `€${parseFloat(price || 0).toFixed(2)}`,
      getCurrencySymbol: () => '€',
    };
  }
  return context;
};

export default CurrencyContext;
